import { useState } from 'react';

import { Instagram, Link, MapPin } from 'lucide-react';

import Image from '@/modules/common/components/Image';
import { sendGTMEvent } from '@/modules/common/services/sendGTMEvent';

import { useDesignerQuery } from '../../gql/__generated/designer.generated';

type DesignerProfileProps = {
  id: string;
};

export default function DesignerProfile({ id }: DesignerProfileProps) {
  const { data, isLoading } = useDesignerQuery({
    id,
  });
  const [showFullDescriptionToggle, setShowFullDescriptionToggle] = useState(false);

  const designer = data?.designer;

  if (isLoading || !designer) {
    return null;
  }

  const handleOutboundClick = (type: string) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      sendGTMEvent({
        event: 'outboundClick',
        eventData: {
          feature: 'Designer collection',
          where: 'Hero',
          type,
          designer: designer.name || '',
        },
      });
    }
  };

  const MAX_DESCRIPTION_LENGTH = 90;
  const descriptionLength = designer.description?.length || 0;

  const shortDescription =
    descriptionLength > MAX_DESCRIPTION_LENGTH ?
      designer.description?.substring(0, MAX_DESCRIPTION_LENGTH) + '...'
    : designer.description;
  const showFullDescription =
    showFullDescriptionToggle || descriptionLength <= MAX_DESCRIPTION_LENGTH;

  return (
    <div className="flex w-full flex-col items-start gap-6 p-0">
      <div className="flex w-full flex-col items-start gap-4 p-0">
        <div className="">
          <div className="flex items-center gap-2">
            <Image
              src={designer.avatar?.url || ''}
              alt={designer.name || 'Designer Avatar'}
              className="block h-[104px] w-[104px] overflow-hidden rounded-full object-cover"
              width={104}
              height={104}
            />
            <div className="flex flex-col items-start justify-between gap-1">
              <div className="flex flex-col items-start gap-1">
                <h2 className="text-[22px] font-bold leading-tight text-black">{designer.name}</h2>
                <p className="text-[18px] leading-[24px] text-gray-800">{designer.role}</p>
              </div>
            </div>
          </div>

          <div className="py-6">
            <p className="hidden text-[18px] leading-[24px] text-gray-800 md:block">
              {designer.description}
            </p>
            <p className="text-lg text-gray-800 md:hidden">
              {showFullDescription ? designer.description : shortDescription}
              <button
                className="ml-1 inline underline transition-opacity hover:opacity-80"
                onClick={() => {
                  const newState = !showFullDescriptionToggle;
                  setShowFullDescriptionToggle(newState);

                  // Send appropriate event based on whether expanding or collapsing
                  if (typeof window !== 'undefined' && window.dataLayer) {
                    sendGTMEvent({
                      event: newState ? 'sectionExpanded' : 'sectionCollapsed',
                      eventData: {
                        feature: 'Designer collection',
                        where: 'Hero',
                        type: 'Designer bio',
                        name: designer.name || '',
                      },
                    });
                  }
                }}
              >
                {showFullDescription ? 'See Less' : 'See More'}
              </button>
            </p>
          </div>

          <div className="grid justify-start">
            {designer.location?.map((loc) => (
              <div className="flex w-full items-center gap-3 py-0.5" key={loc}>
                <MapPin size={32} strokeWidth={1} className="shrink-0 p-1 text-black" />
                <span className="text-base text-black">{loc}</span>
              </div>
            ))}

            {designer.instagramUrl && designer.instagramLabel && (
              <a
                href={designer.instagramUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="outbound-link flex w-full items-center gap-3 py-0.5"
                data-outbound-link-type="Instagram"
                onClick={() => handleOutboundClick('Instagram')}
              >
                <Instagram size={32} strokeWidth={1} className="shrink-0 p-1 text-black" />
                <span className="text-base text-black">{designer.instagramLabel}</span>
              </a>
            )}

            {designer.websiteUrl && designer.websiteLabel && (
              <a
                href={designer.websiteUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="outbound-link flex w-full items-center gap-3 py-0.5"
                data-outbound-link-type="Official site"
                onClick={() => handleOutboundClick('Official site')}
              >
                <Link size={32} strokeWidth={1} className="shrink-0 p-1 text-black" />
                <span className="text-base text-black">{designer.websiteLabel}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
